import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnInit,
  Self,
  ViewChild,
} from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatDivider, MatListItem, MatNavList } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { MatToolbar } from '@angular/material/toolbar';
import { MatTooltip } from '@angular/material/tooltip';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  NavigationStart,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
} from '@angular/router';
import { FinancingPlanType } from '@flexyoo/api/models';
import { AuthenticationService } from '@flexyoo/auth';
import { WhiteLabelService } from '@flexyoo/common';
import {
  EmployeePlanInvitationDataAccessService,
  UserFinancingPlansService,
} from '@flexyoo/employee/data-access';
import { employeeSettingsSelectors } from '@flexyoo/employee/store';
import { FlexyooLetDirective } from '@flexyoo/shared/directives';
import { SidenavStateService } from '@flexyoo/shared/services';
import {
  LoaderComponent,
  TenantGlobalFilterComponent,
  TenantGlobalFilterService,
} from '@flexyoo/shared/ui';
import { TenantYearClosingSettingsService } from '@flexyoo/tenant/data-access';
import { ITenantSettings, YearClosingPossibleTenant } from '@flexyoo/tenant/models';
import { tenantSettingsSelectors } from '@flexyoo/tenant/store';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest, Observable, of, take } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, switchMap, takeUntil } from 'rxjs/operators';
import {
  appRoutesPerRoleDefaults,
  getAppRoutePerRoleDefault,
} from '../app-routes-per-role-defaults.constant';
import { HrNotificationsDropdownComponent } from '../hr/hr-notifications-dropdown/hr-notifications-dropdown.component';
import { NotificationsSidebarComponent } from '../hr/hr-notifications-dropdown/notifications-sidebar/notifications-sidebar.component';
import { NotificationsSidebarService } from '../hr/hr-notifications-dropdown/services/notifications-sidebar.service';
import { TransactionsDataAccessService } from '../hr/hr-transactions/services/transactions.data-access.service';
import { UserRole } from '../libs/admin/users/data-access/admin-users.dto';
import {
  IEmployeePlanInvitationInfo,
  isCafeteriaPlanMenuDisabled,
  isCafeteriaPlanMenuVisible,
  isDualPlanEmployee,
  isMobilityBudgetMenuDisabled,
  isMobilityBudgetMenuVisible,
  isMobilityBudgetOnlyEmployee,
} from '../libs/employee/util/employee-plans-info.util';
import { LanguageEnum } from '../shared/models/constants';
import { RoleEnum } from '../shared/models/general/general-enum';
import { User } from '../shared/models/user-model';
import { NgOnDestroy } from '../shared/ng-on-destroy.service';
import { FlexyooAppVersionDataAccessService } from './flexyoo-app-version/flexyoo-app-version.data-access.service';
import { ImpersonatedBannerComponent } from './impersonated-banner/impersonated-banner.component';
import { SidenavLogoComponent } from './sidenav-logo/sidenav-logo.component';
import {
  ADMIN_LINKS,
  FlexyooNavLinkItem,
  FlexyooNavLinks,
  HR_CAFETERIA_LINKS,
  HR_MB_LINKS,
  OPCO_LINKS,
} from './user-links-per-role.constant';
import { UserMenuComponent } from './user-menu/user-menu.component';

@Component({
  selector: 'app-base-layout',
  templateUrl: 'base-layout.component.html',
  styleUrls: ['base-layout.component.scss'],
  providers: [NgOnDestroy],
  standalone: true,
  imports: [
    MatMenuModule,
    UserMenuComponent,
    FlexyooLetDirective,
    TranslateModule,
    MatTooltip,
    AsyncPipe,
    MatIcon,
    NgClass,
    NgTemplateOutlet,
    HrNotificationsDropdownComponent,
    MatToolbar,
    FlexLayoutModule,
    RouterLink,
    MatSidenavContainer,
    MatSidenav,
    MatNavList,
    MatListItem,
    RouterLinkActive,
    MatDivider,
    ImpersonatedBannerComponent,
    NotificationsSidebarComponent,
    RouterOutlet,
    LoaderComponent,
    MatSidenavContent,
    SidenavLogoComponent,
    MatButton,
    TenantGlobalFilterComponent,
    FormsModule,
  ],
})
export class BaseLayoutComponent implements OnInit, AfterViewInit {
  tenantName: string;
  loading: boolean;
  lang = LanguageEnum;
  private readonly auth = inject(AuthenticationService);
  private readonly flexyooAppVersionService = inject(FlexyooAppVersionDataAccessService);
  private readonly sidenavStateService = inject(SidenavStateService);
  private readonly userFinancingPlansService = inject(UserFinancingPlansService);
  private readonly store = inject(Store);
  private trService = inject(TransactionsDataAccessService);
  private readonly notificationsSidebarService = inject(NotificationsSidebarService);
  private readonly tenantYearClosingSettingsService = inject(TenantYearClosingSettingsService);
  private readonly tenantGlobalFilterService = inject(TenantGlobalFilterService);
  private readonly employeePlanInvitationService = inject(EmployeePlanInvitationDataAccessService);
  protected readonly tenantItems$ = this.tenantGlobalFilterService.tenantFilterItems$;
  protected readonly tenantGlobalFilter$ = this.tenantGlobalFilterService.tenantGlobalFilter$;

  protected readonly isNotificationSidebarVisible$ = this.notificationsSidebarService.isVisible$;
  protected closeNotificationsSidebar(): void {
    this.notificationsSidebarService.toggle(false);
  }

  protected readonly user$: Observable<User> = this.auth.currentUserSubject;

  private readonly employeeInvitation$: Observable<IEmployeePlanInvitationInfo | null> =
    this.user$.pipe(
      switchMap((user) =>
        user?.Role === UserRole.Employee && user.EmployeeId
          ? this.employeePlanInvitationService.loadEmployeePlanInvitation(user.EmployeeId)
          : of(null)
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );

  protected readonly isYearClosingAllowed$ = this.user$.pipe(
    map((user) => user?.Role === UserRole.Hr),
    distinctUntilChanged(),
    switchMap((isHr) =>
      isHr
        ? combineLatest([
            this.tenantYearClosingSettingsService.updateAndLoadTenantYearClosingSettings(),
            this.store.select(tenantSettingsSelectors.selectIsCafeteriaEnabledForAnyTenant),
          ]).pipe(
            map(
              ([isYearClosingEnabled, isCafeteriaEnabled]) =>
                isYearClosingEnabled && isCafeteriaEnabled
            )
          )
        : of(false)
    ),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  protected readonly isEmployeeMenuDisabled$ = combineLatest([
    this.store.select(employeeSettingsSelectors.selectEmployeeSettings),
    this.store.select(tenantSettingsSelectors.selectTenantSettings),
  ]).pipe(
    map(([employeeSettings, tenantSettings]) =>
      isCafeteriaPlanMenuDisabled(employeeSettings, tenantSettings)
    ),
    distinctUntilChanged()
  );

  private readonly isMobilityEmployeeMenuDisabled$ = combineLatest([
    this.store.select(employeeSettingsSelectors.selectEmployeeSettings),
    this.store.select(tenantSettingsSelectors.selectTenantSettings),
  ]).pipe(
    map(([employeeSettings, tenantSettings]) =>
      isMobilityBudgetMenuDisabled(employeeSettings, tenantSettings)
    ),
    distinctUntilChanged()
  );

  protected readonly hrYearClosingBannerInfo$ = combineLatest([
    this.user$,
    this.store.select(tenantSettingsSelectors.selectTenantGroupYearClosingInfo),
    this.store.select(tenantSettingsSelectors.selectTenantSettings),
  ]).pipe(
    map(
      ([user, yearClosingSettings, tenantSettings]: [
        User,
        Record<number, YearClosingPossibleTenant>,
        ITenantSettings,
      ]) => {
        if (
          user?.Role !== UserRole.Hr ||
          user?.TenantId !== tenantSettings?.id ||
          !yearClosingSettings
        ) {
          return null;
        }
        const entries = Object.values(yearClosingSettings);
        const isEnabled = entries.length > 0;

        const isEnabledForCurrentTenant = yearClosingSettings[user.TenantId]?.IsYearClosingModeSet;
        const isEnabledForCurrentTenantMulti = isEnabledForCurrentTenant && entries.length > 1;

        const enabledForAnotherTenant = entries.length === 1 ? entries[0].Name : null;
        const enabledForOtherTenants =
          entries.length > 1 ? `${entries[0].Name} (+${entries.length - 1})` : null;

        return {
          isEnabled,
          isEnabledForCurrentTenant,
          isEnabledForCurrentTenantMulti,
          enabledForAnotherTenant,
          enabledForOtherTenants,
        };
      }
    ),
    distinctUntilChanged()
  );

  protected readonly multiTenantsYearClosingTooltip$ = this.store
    .select(tenantSettingsSelectors.selectTenantGroupYearClosingInfo)
    .pipe(
      map((info: Record<number, YearClosingPossibleTenant>) => {
        return Object.values(info)
          .map((v) => v.Name)
          .join(', ');
      })
    );

  protected readonly isPBPlanEnabled$ = this.store.select(
    tenantSettingsSelectors.selectIsPBFinancingPlanEnabledForAnyTenant
  );

  protected readonly isPBTransactionsExists$ = this.trService
    .getBenefitTransactions(
      {
        financingPlanType: FinancingPlanType.PrimesAndBonuses,
      },
      0,
      25,
      {
        SortByField: '0',
        SortByDirection: '0',
      }
    )
    .pipe(map((res) => res.TotalCount > 0));

  protected readonly isHrCafeteriaTransactionsMenu$ = combineLatest([
    this.store.select(tenantSettingsSelectors.selectIsCafeteriaEnabledForAnyTenant),
    this.isPBPlanEnabled$,
  ]).pipe(
    switchMap(([hasCafeteria, isPBPlanEnabled]) => {
      if (!hasCafeteria) {
        return of(false);
      }

      if (isPBPlanEnabled === false) {
        return this.isPBTransactionsExists$;
      }

      if (isPBPlanEnabled === true) {
        return of(true);
      }

      return of(false);
    }),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  protected readonly isYearClosingBannerVisible$ = combineLatest([
    this.isYearClosingAllowed$,
    this.hrYearClosingBannerInfo$,
  ]).pipe(
    map(
      ([isYearClosingAllowed, hrYearClosingBannerInfo]) =>
        !!isYearClosingAllowed && !!hrYearClosingBannerInfo?.isEnabled
    ),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  protected readonly isTwoPlans$ = this.user$.pipe(
    switchMap((user) =>
      user?.Role === UserRole.Hr
        ? combineLatest([
            this.store.select(tenantSettingsSelectors.selectIsCafeteriaEnabledForAnyTenant),
            this.store.select(tenantSettingsSelectors.selectIsMobilityBudgetEnabledForAnyTenant),
          ]).pipe(map(([hasCafeteria, hasMobility]) => hasCafeteria && hasMobility))
        : this.store
            .select(employeeSettingsSelectors.selectEmployeeSettings)
            .pipe(map((settings) => isDualPlanEmployee(settings)))
    ),
    distinctUntilChanged()
  );

  private readonly isEmployeeMobilityBudgetOnlyNav$ = this.store
    .select(employeeSettingsSelectors.selectEmployeeSettings)
    .pipe(
      map((settings) => isMobilityBudgetOnlyEmployee(settings)),
      distinctUntilChanged()
    );

  private readonly isHrMobilityBudgetOnlyNav$ = combineLatest([
    this.store.select(tenantSettingsSelectors.selectIsMobilityBudgetEnabledForAnyTenant),
    this.store.select(tenantSettingsSelectors.selectIsCafeteriaEnabledForAnyTenant),
  ]).pipe(
    map(([hasMobility, hasCafeteria]) => hasMobility && !hasCafeteria),
    distinctUntilChanged()
  );

  protected readonly showEmployeeBudgetMenuItem$ = this.user$.pipe(
    switchMap((user) =>
      user?.Role === UserRole.Employee
        ? this.userFinancingPlansService.userActivePlansInfo$.pipe(
            map((info) => info[FinancingPlanType.PrimesAndBonuses])
          )
        : of(false)
    ),
    distinctUntilChanged()
  );

  private readonly employeeLinks$: Observable<FlexyooNavLinks[]> = combineLatest([
    this.isEmployeeMenuDisabled$.pipe(distinctUntilChanged()),
    this.isMobilityEmployeeMenuDisabled$.pipe(distinctUntilChanged()),
    this.showEmployeeBudgetMenuItem$.pipe(distinctUntilChanged()),
    this.isTwoPlans$.pipe(distinctUntilChanged()),
    this.isEmployeeMobilityBudgetOnlyNav$.pipe(distinctUntilChanged()),
    this.employeeInvitation$.pipe(
      distinctUntilChanged(
        (prev, curr) =>
          prev?.cafeteriaInvitationDate === curr?.cafeteriaInvitationDate &&
          prev?.mobilityBudgetInvitationDate === curr?.mobilityBudgetInvitationDate
      )
    ),
    this.store.select(employeeSettingsSelectors.selectEmployeeSettings),
  ]).pipe(
    map(
      ([
        isEmployeeMenuDisabled,
        isMobilityEmployeeMenuDisabled,
        showEmployeeBudgetMenuItem,
        isGroupedPlanNav,
        isMobilityBudgetOnlyNav,
        employeeInvitation,
        employeeSettings,
      ]) => {
        const innerCafLinks: FlexyooNavLinkItem[] = [
          {
            role: RoleEnum.Employee,
            link: '/employee',
            icon: 'list_alt',
            translationsKey: 'base-layout.my-plan',
            isExpandable: false,
            isDisabled: isEmployeeMenuDisabled,
          },
          {
            role: RoleEnum.Employee,
            link: '/employee-benefits',
            icon: 'redeem',
            translationsKey: 'base-layout.my-benefits',
            isExpandable: false,
            isDisabled: isEmployeeMenuDisabled,
          },
        ];

        const employeeBudgetMenuItem: FlexyooNavLinkItem[] = showEmployeeBudgetMenuItem
          ? [
              {
                role: RoleEnum.Employee,
                link: '/employee-budget',
                icon: 'euro_symbol',
                translationsKey: 'base-layout.my-budget',
                isExpandable: false,
              },
            ]
          : [];

        const innerMBLinks: FlexyooNavLinkItem[] = [
          {
            role: RoleEnum.Employee,
            link: '/employee-mbp/overview',
            icon: 'list_alt',
            translationsKey: 'base-layout.my-mobility-plan',
            isExpandable: false,
            isDisabled: isMobilityEmployeeMenuDisabled,
          },
        ];

        const emplLinks: FlexyooNavLinks[] = [];
        if (isGroupedPlanNav) {
          if (isCafeteriaPlanMenuVisible(employeeSettings, employeeInvitation)) {
            emplLinks.push({
              role: RoleEnum.Employee,
              icon: 'fo-cafeteria-plan',
              isCustomIcon: true,
              translationsKey: 'financing-plans.cafeteria-plan',
              children: [...innerCafLinks, ...employeeBudgetMenuItem],
              isExpandable: true,
              isOpened: false,
            });
          }

          if (isMobilityBudgetMenuVisible(employeeSettings, employeeInvitation)) {
            emplLinks.push({
              role: RoleEnum.Employee,
              icon: 'directions_car',
              translationsKey: 'financing-plans.mobility-budget',
              children: innerMBLinks,
              isExpandable: true,
              isOpened: false,
            });
          }
        } else if (isMobilityBudgetOnlyNav) {
          emplLinks.push(...innerMBLinks);
        } else {
          emplLinks.push(...innerCafLinks, ...employeeBudgetMenuItem);
        }

        return emplLinks;
      }
    )
  );

  private readonly hrLinks$: Observable<FlexyooNavLinks[]> = combineLatest([
    this.isHrCafeteriaTransactionsMenu$.pipe(distinctUntilChanged()),
    this.isTwoPlans$.pipe(distinctUntilChanged()),
    this.isHrMobilityBudgetOnlyNav$.pipe(distinctUntilChanged()),
  ]).pipe(
    map(([isHrCafeteriaTransactionsMenu, isGroupedPlanNav, isMobilityBudgetOnlyNav]) => {
      const hrLinks: FlexyooNavLinks[] = [
        {
          role: RoleEnum.HR,
          link: '/hr/dashboard',
          icon: 'fo-dashboard',
          isCustomIcon: true,
          translationsKey: 'base-layout.dashboard',
          isExpandable: false,
        },
        {
          role: RoleEnum.HR,
          link: '/hr/employees',
          icon: 'people_outline',
          translationsKey: 'base-layout.employees',
          isExpandable: false,
        },
      ];

      const hrTransactionsItem: FlexyooNavLinkItem[] = isHrCafeteriaTransactionsMenu
        ? [
            {
              role: RoleEnum.HR,
              link: '/hr/budgets-management',
              icon: 'fo-budgets',
              isCustomIcon: true,
              translationsKey: 'base-layout.budgets-management',
              isExpandable: false,
            },
          ]
        : [];

      if (isGroupedPlanNav) {
        hrLinks.push(
          {
            role: RoleEnum.HR,
            icon: 'fo-cafeteria-plan',
            isCustomIcon: true,
            translationsKey: 'financing-plans.cafeteria-plan',
            children: [...hrTransactionsItem, ...HR_CAFETERIA_LINKS],
            isExpandable: true,
            isOpened: false,
          },
          {
            role: RoleEnum.HR,
            icon: 'directions_car',
            translationsKey: 'financing-plans.mobility-budget',
            children: HR_MB_LINKS,
            isExpandable: true,
            isOpened: false,
          }
        );
      } else if (isMobilityBudgetOnlyNav) {
        hrLinks.push(...HR_MB_LINKS);
      } else {
        hrLinks.push(...hrTransactionsItem, ...HR_CAFETERIA_LINKS);
      }

      return hrLinks;
    })
  );

  protected readonly links$: Observable<FlexyooNavLinks[]> = this.user$.pipe(
    map((user) => user?.Role),
    distinctUntilChanged(),
    switchMap((userRole) => {
      if (userRole === RoleEnum.Admin) {
        return of(ADMIN_LINKS);
      }
      if (userRole === RoleEnum.Opco) {
        return of(OPCO_LINKS);
      }
      if (userRole === RoleEnum.HR) {
        return this.hrLinks$;
      }
      if (userRole === RoleEnum.Employee) {
        return this.employeeLinks$;
      }
    })
  );

  @ViewChild('logoEl', { static: false }) logoEl: ElementRef;
  @ViewChild('sidenav') sidenav: MatSidenav;
  public readonly isExpanded$ = this.sidenavStateService.isExpanded$;
  public showSubmenu = false;
  public isShowing = false;
  public showSubmenu2 = false;
  public isShowing2 = false;
  public readonly isSmallView$ = inject(BreakpointObserver)
    .observe([Breakpoints.XSmall, Breakpoints.Small])
    .pipe(
      map((result) => result.matches),
      distinctUntilChanged(),
      shareReplay({ refCount: true, bufferSize: 1 })
    );

  constructor(
    protected router: Router,
    public wlc: WhiteLabelService,
    @Self() private readonly onDestroy$: NgOnDestroy
  ) {
    this.tenantName = '';
    this.loading = true;

    router.events.pipe(takeUntil(this.onDestroy$)).subscribe((routerEvent) => {
      this.checkRouterEvent(routerEvent);
    });
  }

  private get user() {
    return this.auth.getCurrentUser();
  }

  get currentYear() {
    return new Date().getFullYear();
  }

  ngOnInit() {
    this.tenantName = this.user.TenantName;

    if (this.user.Role === RoleEnum.Admin) {
      this.tenantName = 'Flexyoo';
    }
  }

  ngAfterViewInit() {
    this.user$.pipe(take(1)).subscribe(() => {
      setTimeout(() => {
        this.flexyooAppVersionService
          .handleClickToShowDialog(this.logoEl?.nativeElement, this.goToBaseRoute.bind(this))
          .pipe(takeUntil(this.onDestroy$))
          .subscribe();
      }, 10);
    });
  }

  public setIsExpanded(value: boolean, isEmployeeMenuDisabled?: boolean, clickEv?: Event): void {
    if (isEmployeeMenuDisabled) {
      clickEv?.preventDefault();
      return;
    }
    this.sidenavStateService.setIsExpanded(value);
  }

  private checkRouterEvent(routerEvent): void {
    if (routerEvent instanceof NavigationStart) {
      this.loading = true;
    }

    // NavigationCancel/NavigationError are the only events a navigation stopped by a guard
    // produces - without them the loader would never be hidden again.
    if (
      routerEvent instanceof NavigationEnd ||
      routerEvent instanceof NavigationCancel ||
      routerEvent instanceof NavigationError ||
      routerEvent instanceof NavigationSkipped
    ) {
      this.loading = false;
    }
  }

  onLogout() {
    this.auth.logout();
  }

  goToBaseRoute() {
    if (this.user.Role === RoleEnum.Employee) {
      combineLatest([
        this.store.select(employeeSettingsSelectors.selectEmployeeSettings),
        this.store.select(tenantSettingsSelectors.selectTenantSettings),
        this.employeePlanInvitationService.loadEmployeePlanInvitation(this.user.EmployeeId),
      ])
        .pipe(take(1))
        .subscribe(([employeeSettings, tenantSettings, invitation]) => {
          this.router.navigateByUrl(
            getAppRoutePerRoleDefault(
              RoleEnum.Employee,
              employeeSettings,
              tenantSettings,
              invitation
            )
          );
        });
      return;
    }

    if ([RoleEnum.Admin, RoleEnum.HR].includes(this.user.Role as RoleEnum)) {
      this.router.navigateByUrl(appRoutesPerRoleDefaults[this.user.Role]);
      return;
    }

    this.router.navigateByUrl('/login');
  }
}
